import { Drawer, Radio, Checkbox, Space, Typography, Button, message, Modal, Input } from 'antd'
import { Warning } from '@phosphor-icons/react'
import styled from 'styled-components'
import DeliverySchedule from '../scheduler.component'
import { Job, Slot, Slots } from '../../domain/job'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { slotsUseCase } from '../../application/slots.use-case'
import { odinJobRepository } from '../odin-job.repository'
import { getReasons } from '../../../../../domain/flag-management/flag-management.services'
import { REASON_PARAMS } from '../../../../../utils/constants'
import { Reason } from '../../domain/reason'
import { JobItem } from '../../domain/items'
import { useTranslation } from 'react-i18next'
import { itemRepository } from '../odin-nebula-item.repository'

const { Text, Title } = Typography
const MAX_DAYS_RANGE = 7
const SLOTS_INITIAL_STATE = {
  static: undefined,
  express: undefined
}

function RescheduleJob({ visible: isVisible, job, items, handleClose, handleOnReschedule }: RescheduleJobProps) {
  const [loading, setLoading] = useState(false)
  const { t } = useTranslation()
  const [showModal, setShowModal] = useState(false)
  const [resetSteps, setResetSteps] = useState(false)
  const [slots, setSlots] = useState<Slots>(SLOTS_INITIAL_STATE)
  const [reasons, setReasons] = useState<Reason[]>()
  const [selectedReasonId, setSelectedReasonId] = useState<string>()
  const [comment, setComment] = useState<string>()
  const [showMinimunCharactersError, setShowMinimumCharactersError] = useState(false)
  const [showEmptyErrors, setShowEmptyErrors] = useState(false)
  const [selectedSlot, setSelectedSlot] = useState<Slot>()
  const [isAllowedAllSlots, setIsAllowedAllSlots] = useState(true)

  const handleGetSlots = useCallback(async () => {
    try {
      const slots = await slotsUseCase(odinJobRepository, itemRepository).getAvailableSlots({
        allowWithoutCapacity: isAllowedAllSlots,
        job: {
          jobId: `${job.id}`,
          storeTzSlotTo: job.storeTzSlotTo!,
          storeTzSlotFrom: job.storeTzSlotFrom!
        },
        items: items.map((item) => ({ id: item.itemId, quantity: item.quantity })),
        dayRange: MAX_DAYS_RANGE
      })
      setSlots(slots)
      setSelectedSlot(undefined)
    } catch (_) {
      setSlots(SLOTS_INITIAL_STATE)
      setSelectedSlot(undefined)
    }
  }, [isAllowedAllSlots])

  const handleGetReasons = useCallback(async () => {
    try {
      const reasons = (await getReasons(REASON_PARAMS.RESCHEDULE)) as Reason[]
      setReasons(reasons)
    } catch (_) {
      setReasons(undefined)
    }
  }, [])

  const isCommentMandatory = useMemo(
    () => reasons?.find((reason) => reason.id === selectedReasonId)?.metadata.openQuestion,
    [reasons, selectedReasonId]
  )

  const loadInfo = useCallback(async () => {
    handleGetSlots()
    if (reasons) return
    handleGetReasons()
  }, [job, items, isAllowedAllSlots])

  useEffect(() => {
    if (job && items && isVisible) {
      loadInfo()
    }
  }, [isVisible, isAllowedAllSlots])

  const isOpenReason = useMemo(
    () => reasons?.find((reason) => reason.id === selectedReasonId)?.metadata.openQuestion,
    [reasons, selectedReasonId]
  )

  const validOperationModelForStepsReset = useMemo(
    () =>
      job?.operationModel
        ? [
            'PICK_AND_DELIVERY_WITH_STORAGE',
            'PICK_AND_DELIVERY_WITH_STORAGE_NO_TRANSFER',
            'ZONE_PICKING_AND_DELIVERY_WITH_STORAGE'
          ].includes(job.operationModel)
        : false,
    [job?.operationModel]
  )

  const validStepsForStepsReset = useMemo(
    () =>
      job?.tasks
        ? job.tasks.some(
            (task) =>
              (task.name === 'PICK_UP_FOR_DELIVERY' || task.name === 'DELIVERY_WITH_STORAGE') && task.status === 'DOING'
          )
        : false,
    [job?.tasks]
  )

  const reschedule = useCallback(async () => {
    const jobId = job.id
    setLoading(true)
    try {
      const selectedReason = reasons?.find((reason) => reason.id === selectedReasonId)
      await odinJobRepository.reschedule({
        jobId: `${jobId}`,
        slot: {
          slotId: selectedSlot?.id,
          from: selectedSlot.from,
          to: selectedSlot.to
        },
        comment: isOpenReason ? comment : selectedReason.title,
        otherReason: isOpenReason,
        resetSteps,
        reasonId: selectedReasonId,
        reasonType: REASON_PARAMS.RESCHEDULE
      })
      setLoading(false)
      handleOnReschedule()
      message.success(t('jobs.jobRescheduleV2.successMessage'))
    } catch (error) {
      message.error(t('jobs.jobRescheduleV2.errorMessage'))
    } finally {
      setLoading(false)
    }
  }, [selectedReasonId, selectedSlot?.from, selectedSlot?.to, resetSteps, comment, isOpenReason])

  const handleOnClickSave = useCallback(() => {
    setShowEmptyErrors(false)
    setShowMinimumCharactersError(false)
    const isEmptyError = !selectedSlot?.from || !selectedSlot?.to || !selectedReasonId || (isOpenReason && !comment)
    const isCommentValid = comment?.length >= 5
    if ((isOpenReason && !isCommentValid) || isEmptyError || resetSteps) {
      if (!isCommentValid) setShowMinimumCharactersError(true)
      if (isEmptyError) setShowEmptyErrors(true)
      if (resetSteps) setShowModal(true)
      return
    }

    reschedule()
  }, [resetSteps, isOpenReason, comment, selectedSlot, selectedReasonId])

  const handleOnChangeComment = useCallback((e) => {
    setComment(e.target.value)
  }, [])

  const handleToggleCapacityCheckBox = useCallback(() => {
    setIsAllowedAllSlots((_isAllowedAllSlots) => !_isAllowedAllSlots)
    setSelectedSlot(undefined)
  }, [])

  return (
    <Drawer
      title={t('jobs.jobRescheduleV2.title')}
      width={650}
      destroyOnClose={true}
      onClose={handleClose}
      visible={isVisible}
      footer={
        <>
          <Button onClick={handleClose}>{t('jobs.jobRescheduleV2.cancelButton')}</Button>
          <Button loading={loading} type='primary' onClick={handleOnClickSave} disabled={slots === SLOTS_INITIAL_STATE}>
            {t('jobs.jobRescheduleV2.saveButton')}
          </Button>
        </>
      }
      footerStyle={{
        display: 'flex',
        height: '64px',
        padding: '16px var(--spacing-6, 24px)',
        alignItems: 'center',
        gap: '12px',
        justifyContent: 'flex-end',
        flexShrink: 0,
        alignSelf: 'stretch'
      }}
    >
      <div
        style={{
          display: 'flex',
          padding: 'var(--spacing-6, 24px)',
          flexDirection: 'column',
          width: 'fit-content',
          alignItems: 'flex-start',
          gap: 'var(--spacing-6, 24px)',
          flex: '1 0 0',
          alignSelf: 'stretch'
        }}
      >
        <Box>
          <Title
            style={{
              margin: '0'
            }}
            level={5}
          >
            {t('jobs.jobRescheduleV2.selectScheduleTitle')}
          </Title>
          <Checkbox checked={isAllowedAllSlots} onChange={handleToggleCapacityCheckBox}>
            <Text>{t('jobs.jobRescheduleV2.seeAllSlots')}</Text>
          </Checkbox>
          {isAllowedAllSlots && (
            <div
              style={{
                display: 'flex',
                padding: '8px 12px',
                alignItems: 'center',
                gap: '8px',
                alignSelf: 'stretch',
                background: '#FFFBE6'
              }}
            >
              <Warning color='#FAAD14' size={40} />
              <Text>{t('jobs.jobRescheduleV2.seeAllSlotsWarning')}</Text>
            </div>
          )}
          <div
            style={{
              width: '100%',
              minWidth: '520.234px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <DeliverySchedule
              timezone={job.storeTimezone!}
              showEmptyErrors={showEmptyErrors}
              onChange={({ slot }) => setSelectedSlot(slot)}
              slots={slots}
            />
          </div>
        </Box>
        {validOperationModelForStepsReset && validStepsForStepsReset ? (
          <Box>
            <Checkbox checked={resetSteps} onChange={(e) => setResetSteps(e.target.checked)}>
              <Text>{t('jobs.jobRescheduleV2.returnToStoreCheckbox')}</Text>
            </Checkbox>
            <Text type='secondary'>{t('jobs.jobRescheduleV2.returnToStoreInfo')}</Text>
          </Box>
        ) : null}
        <Box>
          <Title level={5}>{t('jobs.jobRescheduleV2.selectReasonTitle')}</Title>
          {showEmptyErrors && !selectedReasonId && <Text type='danger'>{t('jobs.jobRescheduleV2.reasonError')}</Text>}
          <Radio.Group defaultValue={selectedReasonId} onChange={(e) => setSelectedReasonId(e.target.value)}>
            <Space direction='vertical'>
              {reasons?.map((reason) => (
                <Radio key={reason.id} value={reason.id}>
                  {reason.title}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
          {isCommentMandatory && (
            <>
              {isOpenReason && !comment && showEmptyErrors && (
                <Text type='danger'>{t('jobs.jobRescheduleV2.describeReasonError')}</Text>
              )}
              <Input.TextArea
                value={comment}
                placeholder={t('jobs.jobRescheduleV2.reasonPlaceholder')}
                rows={2}
                onChange={handleOnChangeComment}
              />
              {isOpenReason && showMinimunCharactersError && comment?.length < 5 && (
                <Text type='danger'>{t('jobs.jobRescheduleV2.minimumCharactersError')}</Text>
              )}
            </>
          )}
        </Box>
      </div>
      <Modal
        visible={showModal}
        title={t('jobs.jobRescheduleV2.modal.title')}
        footer={[
          <Button key='footer-modal-reschedule' type='primary' onClick={reschedule} loading={loading}>
            {t('jobs.jobRescheduleV2.modal.confirmButton')}
          </Button>
        ]}
        onCancel={() => setShowModal(false)}
      >
        {t('jobs.jobRescheduleV2.modal.info')}
        <strong>{t('jobs.jobRescheduleV2.modal.infoStrong')}</strong>
      </Modal>
    </Drawer>
  )
}

const Box = styled.div<{ alignItems?: string }>`
  display: flex;
  padding: 12px 16px;
  width: 550.234px;
  align-items: ${({ alignItems }) => (alignItems ? alignItems : 'flex-start')};
  flex-direction: column;
  border-radius: var(--spacing-2, 8px);
  border: var(--spacing-0, 1px) solid var(--Neutral-5, #e3e7ef);
  gap: var(--spacing-1, 10px);
  align-self: stretch;
`

export default RescheduleJob

type RescheduleJobProps = {
  items: JobItem[]
  visible: boolean
  handleClose: () => void
  handleOnReschedule: () => void
  job: Job
}
